// Login.jsx - Works for Microsoft, Google, Apple, and all email domains

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import { setEmail as setEmailAction } from "./userSlice"; // FIX: renamed on import to avoid collision with local useState setter
import { setSession } from "./utils/session";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-project360-insights.haqdarshak.com';

  // Function to log login to backend
  const logUserLogin = async (emailToLog) => {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi/auth/log-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailToLog
        })
      });

      const data = await response.json();
      if (data.success) {
        console.log('Login logged successfully:', data);
      } else {
        console.error('Failed to log login:', data.error);
      }
    } catch (err) {
      console.error('Error logging login:', err);
      // Don't block login if logging fails
    }
  };

  // Handle Manual Email/Password Login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    try {
      // NOTE: sending password as a GET query param is not secure (it ends up in
      // server logs, browser history, and proxy logs). Consider switching this
      // endpoint to POST with a JSON body when you get a chance.
      const loginUrl = `${API_BASE_URL}/api/microsoft-login/?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

      console.log('🔐 Login URL:', loginUrl);

      const response = await fetch(loginUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      console.log('📡 Response Status:', response.status);
      console.log('📡 Response OK:', response.ok);

      // Get the response text first
      const responseText = await response.text();
      console.log('📄 Raw Response:', responseText);

      // Parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('✅ Parsed Data:', data);
      } catch (parseError) {
        console.error('❌ JSON Parse Error:', parseError, 'Raw text was:', responseText);
        setError('Server returned an invalid response. Please try again.');
        setLoading(false);
        return;
      }

      // Check if login was successful
      if (response.ok && data.success) {
        // ✅ Save email in session
        setSession({ email: data.email });

        // ✅ Save in Redux
        dispatch(setEmailAction(data.email));

        // ✅ Log the login to backend (non-blocking)
        await logUserLogin(data.email);

        console.log('🎉 Login successful! Redirecting to Project-Summary...');

        // Navigate to dashboard
        navigate("/Project-Summary");
      } else {
        // Show error from backend
        const errorMessage = data.error || data.message || "Login failed. Please try again.";
        console.error('❌ Login failed:', errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      // FIX: log the actual error (was shadowed before) so real failures are visible in the console
      console.error('❌ Login error:', err);
      setError(`An error occurred: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google SSO Login (unchanged)
  const handleGoogleLogin = async (response) => {
    try {
      const credentialResponse = JSON.parse(
        atob(response.credential.split(".")[1])
      );

      const userEmail = credentialResponse.email;

      // ✅ Save email in session
      setSession({ email: userEmail });

      // ✅ Save in Redux
      dispatch(setEmailAction(userEmail));

      // ✅ Log the login to backend (non-blocking)
      await logUserLogin(userEmail);

      // Navigate to dashboard
      navigate("/Project-Summary");
    } catch (err) {
      console.error('Login error:', err);
      alert('Login failed. Please try again.');
    }
  };

  return (
    <div className="relative w-full h-screen flex items-center justify-center bg-pink-100">
      <div className="bg-pink-200 p-8 rounded-2xl shadow-lg w-96 text-center">
        <h1 className="text-xl font-bold text-gray-800">हक़दर्शक</h1>
        <h2 className="text-lg font-semibold text-gray-700 mt-2">Haqdarshak</h2>
        <p className="text-gray-600 mt-2">
          Welcome to Haqdarshak Analytics
        </p>

        {!showEmailLogin ? (
          <div className="mt-6 space-y-4">
            {/* Google SSO Login Button - UNCHANGED */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => alert("Google Sign-in failed")}
              />
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-pink-200 text-gray-500">Or</span>
              </div>
            </div>

            {/* Manual Email Login Button */}
            <button
              onClick={() => {
                setShowEmailLogin(true);
                setError("");
              }}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-2 px-4 border border-gray-300 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
              Sign in with Email
            </button>
          </div>
        ) : (
          <div className="mt-6">
            {/* Back Button */}
            <button
              onClick={() => {
                setShowEmailLogin(false);
                setError("");
                setEmail("");
                setPassword("");
              }}
              className="float-left text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>

            <h3 className="text-lg font-semibold text-gray-700 mb-4">Sign in with Email</h3>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter Your Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <input
                  type="password"
                  placeholder="Enter Your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;